import { UsagePaywall } from './usage-paywall';
import { ConsultForm } from './consult-form';
import { CheckoutStep } from './checkout-step';
import { LicensedDone } from './licensed-done';
import { ReflexiveQuestion } from './reflexive-question';

import { IntentSelector } from './intent-selector';

import { useFreemiumGateFlow } from '../hooks/use-freemium-gate-flow';

import {
  getFreemiumCheckoutFeatures,
  getFreemiumIntentFlow,
  resolveFreemiumTier,
  resolveFreemiumTiers,
} from '@shared';

interface FreemiumPaywallFlowProps {
  clientId: string;
  clientFirstName: string;
  onExit: () => void;
}

export function FreemiumPaywallFlow({
  clientId,
  clientFirstName,
  onExit,
}: FreemiumPaywallFlowProps) {
  const {
    step,
    intent,
    reflexiveAnswer,
    selectedTier,
    clientSecret,
    setIntent,
    goToIntent,
    goToReflexive,
    goToPaywall,
    goToGuidedPlanning,
    continueFromIntent,
    selectReflexive,
    selectTier,
    submitConsult,
    isPending,
    pendingTier,
  } = useFreemiumGateFlow(clientId);

  if (!intent || step === 'intent') {
    return (
      <IntentSelector
        clientFirstName={clientFirstName}
        value={intent}
        onChange={setIntent}
        onContinue={continueFromIntent}
        onBack={onExit}
      />
    );
  }

  const flow = getFreemiumIntentFlow(intent);

  if (step === 'reflexive' && flow.question) {
    return (
      <ReflexiveQuestion
        prompt={flow.question.prompt}
        options={flow.question.options}
        onSelect={selectReflexive}
        onBack={goToIntent}
      />
    );
  }

  if (step === 'consult') {
    return (
      <ConsultForm
        prompt={flow.freeTextPrompt ?? 'Tell us a bit more.'}
        onSubmit={submitConsult}
        onBack={goToIntent}
        isPending={isPending}
      />
    );
  }

  if (step === 'paywall') {
    const tiers = resolveFreemiumTiers(intent, reflexiveAnswer);
    return (
      <UsagePaywall
        tiers={tiers}
        onSelectTier={selectTier}
        onBack={
          flow.kind === 'guidedPlanningDeferred'
            ? goToGuidedPlanning
            : flow.kind === 'reflexive'
              ? goToReflexive
              : goToIntent
        }
        isPending={isPending}
        pendingTier={pendingTier}
      />
    );
  }

  if (step === 'checkout') {
    const tierConfig = selectedTier
      ? resolveFreemiumTier(intent, reflexiveAnswer, selectedTier)
      : undefined;
    if (clientSecret && tierConfig && selectedTier) {
      const checkoutConfig = {
        ...tierConfig,
        features: getFreemiumCheckoutFeatures(intent, reflexiveAnswer, selectedTier),
      };
      return (
        <CheckoutStep
          config={checkoutConfig}
          clientSecret={clientSecret}
          onBack={() => goToPaywall(reflexiveAnswer)}
        />
      );
    }
    return (
      <div className="flex flex-col items-center gap-4 py-16">
        <div className="border-primary size-10 animate-spin rounded-full border-4 border-t-transparent" />
        <p className="text-foreground font-medium">Preparing secure checkout…</p>
      </div>
    );
  }

  return (
    <LicensedDone
      clientId={clientId}
      clientFirstName={clientFirstName}
      variant={flow.kind === 'consultHandoff' ? 'consult' : 'licensed'}
    />
  );
}
